import React, { useEffect, useRef } from 'react';
import { Button } from '@vibe/core';
import type { ScheduleItem } from '../../stores/scheduleStore';
import { useWorkloadStore } from '../../stores/workloadStore';
import { computeRemainingPerPerson, isItemOverlogged } from '../../services/workload-data.service';
import { formatHoursMinutes } from '../../utils/timesheet-capacity';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface TaskInfoTooltipProps {
  task: ScheduleItem;
  assigneeName: string;
  /** Parent item name (shown for subitems) */
  parentItemName?: string;
  /** Viewport position for the tooltip */
  position: { x: number; y: number };
  /** When true, board name is shown on the tooltip (multi-board workspace mode) */
  isMultiBoard?: boolean;
  onEdit: (taskId: string) => void;
  onLogWork: (taskId: string) => void;
  onViewWorklogs: (mondayItemId: number, position: { x: number; y: number }) => void;
  onDuplicate: (taskId: string) => void;
  onUnplan: (taskId: string) => void;
  onOpenInMonday: (mondayItemId: number) => void;
  onClose: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const TaskInfoTooltip: React.FC<TaskInfoTooltipProps> = ({
  task,
  assigneeName,
  parentItemName,
  position,
  isMultiBoard,
  onEdit,
  onLogWork,
  onViewWorklogs,
  onDuplicate,
  onUnplan,
  onOpenInMonday,
  onClose,
}) => {
  const curIndicator = useWorkloadStore((s) => s.indicatorType);
  const worklogsByItem = useWorkloadStore((s) => s.worklogsByItem);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Close tooltip on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    // Use setTimeout to avoid immediately closing from the same click that opened it
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Close on mouse leave with a short delay (allows moving to buttons inside tooltip)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleMouseLeave = () => {
    closeTimerRef.current = setTimeout(() => onClose(), 400);
  };
  const handleMouseEnter = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  // Adjust position to stay within viewport
  useEffect(() => {
    if (!tooltipRef.current) return;
    const el = tooltipRef.current;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    if (rect.right > vw - 8) {
      el.style.left = `${vw - rect.width - 8}px`;
    }
    if (rect.bottom > vh - 8) {
      el.style.top = `${vh - rect.height - 8}px`;
    }
  }, [position]);

  // Format date range
  const dateRange = task.startDate === task.endDate
    ? task.startDate
    : `${task.startDate} → ${task.endDate}`;

  return (
    <div
      ref={tooltipRef}
      className="rp-task-info-tooltip"
      style={{ top: position.y, left: position.x }}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
    >
      {/* Header: board name (multi-board) + group name */}
      {(isMultiBoard && task.boardName) || task.groupName ? (
        <div className="rp-task-info-tooltip__header">
          {isMultiBoard && task.boardName && (
            <span className="rp-task-info-tooltip__board-name">{task.boardName}</span>
          )}
          {isMultiBoard && task.boardName && task.groupName && (
            <span className="rp-task-info-tooltip__header-sep">/</span>
          )}
          {task.groupName && (
            <span className="rp-task-info-tooltip__group-name">{task.groupName}</span>
          )}
        </div>
      ) : null}

      {/* Parent item name (subitems only) */}
      {parentItemName && (
        <div className="rp-task-info-tooltip__parent-name">{parentItemName}</div>
      )}

      {/* Item name (clickable — opens in monday.com) */}
      <div
        className="rp-task-info-tooltip__name rp-task-info-tooltip__name--clickable"
        onClick={() => { onOpenInMonday(task.mondayItemId); onClose(); }}
      >
        {task.name}
      </div>

      {/* Attributes */}
      <div className="rp-task-info-tooltip__attrs">
        {(() => {
          const isRemainingMode = curIndicator === 'remaining_estimate' || curIndicator === 'worklog_workload' || curIndicator === 'availability';
          const itemWorklogs = isRemainingMode
            ? (worklogsByItem.get(String(task.mondayItemId)) ?? 0)
            : 0;
          // Hide attributes only when there are no hours AND no worklogs
          if (task.hours <= 0 && itemWorklogs <= 0) return null;
          const suffix = task.workHourFormat === 'per_day' ? 'h/d' : 'h';

          if (isRemainingMode && task.workHourFormat === 'total') {
            const originalTotal = task.originalTotalHours ?? task.hours;
            const perPersonDist = useWorkloadStore.getState().perPersonDistribution;
            const effectiveAssigneeCount = perPersonDist ? (task.assigneeCount ?? 1) : 1;
            const remaining = Math.max(0, originalTotal - itemWorklogs);
            const remainingPerPerson = computeRemainingPerPerson(originalTotal, itemWorklogs, effectiveAssigneeCount);
            const isOverlogged = isItemOverlogged(originalTotal, itemWorklogs);
            return (
              <>
                <div className="rp-task-info-tooltip__attr">
                  <span className="rp-task-info-tooltip__attr-label">Original Estimate</span>
                  <span className="rp-task-info-tooltip__attr-value">{formatHoursMinutes(originalTotal)}</span>
                </div>
                <div className="rp-task-info-tooltip__attr">
                  <span className="rp-task-info-tooltip__attr-label">Logged</span>
                  <span
                    className={`rp-task-info-tooltip__attr-value rp-task-info-tooltip__attr-value--link${isOverlogged ? ' rp-task-info-tooltip__attr-value--overlogged' : ''}`}
                    onClick={(e) => {
                      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                      onViewWorklogs(task.mondayItemId, { x: rect.right + 8, y: rect.top });
                      onClose();
                    }}
                  >
                    {formatHoursMinutes(itemWorklogs)}
                  </span>
                </div>
                <div className="rp-task-info-tooltip__attr">
                  <span className="rp-task-info-tooltip__attr-label">Remaining</span>
                  <span className="rp-task-info-tooltip__attr-value">{remaining === 0 ? '0h' : formatHoursMinutes(remaining)}</span>
                </div>
                {effectiveAssigneeCount > 1 && (
                  <div className="rp-task-info-tooltip__attr">
                    <span className="rp-task-info-tooltip__attr-label">Your allocation</span>
                    <span className="rp-task-info-tooltip__attr-value">{formatHoursMinutes(remainingPerPerson)}</span>
                  </div>
                )}
                {isOverlogged && (
                  <div className="rp-task-info-tooltip__overlog-warning">
                    &#x26A0; Logged {formatHoursMinutes(itemWorklogs - originalTotal)} over estimate
                  </div>
                )}
              </>
            );
          }
          if (isRemainingMode && task.workHourFormat === 'per_day') {
            return (
              <>
                <div className="rp-task-info-tooltip__attr">
                  <span className="rp-task-info-tooltip__attr-label">Original Estimate</span>
                  <span className="rp-task-info-tooltip__attr-value">{formatHoursMinutes(task.hours)}{task.workHourFormat === 'per_day' ? '/d' : ''}</span>
                </div>
                {itemWorklogs > 0 && (
                  <div className="rp-task-info-tooltip__attr">
                    <span className="rp-task-info-tooltip__attr-label">Total Logged</span>
                    <span
                      className="rp-task-info-tooltip__attr-value rp-task-info-tooltip__attr-value--link"
                      onClick={(e) => {
                        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                        onViewWorklogs(task.mondayItemId, { x: rect.right + 8, y: rect.top });
                        onClose();
                      }}
                    >
                      {formatHoursMinutes(itemWorklogs)}
                    </span>
                  </div>
                )}
              </>
            );
          }
          return (
            <div className="rp-task-info-tooltip__attr">
              <span className="rp-task-info-tooltip__attr-label">Hours</span>
              <span className="rp-task-info-tooltip__attr-value">{formatHoursMinutes(task.hours)}{task.workHourFormat === 'per_day' ? '/d' : ''}</span>
            </div>
          );
        })()}

        {task.status && (
          <div className="rp-task-info-tooltip__attr">
            <span className="rp-task-info-tooltip__attr-label">Status</span>
            <span className="rp-task-info-tooltip__attr-value rp-task-info-tooltip__status">
              {task.status}
            </span>
          </div>
        )}

        <div className="rp-task-info-tooltip__attr">
          <span className="rp-task-info-tooltip__attr-label">Assignee</span>
          <span className="rp-task-info-tooltip__attr-value">{assigneeName}</span>
        </div>

        <div className="rp-task-info-tooltip__attr">
          <span className="rp-task-info-tooltip__attr-label">Timeline</span>
          <span className="rp-task-info-tooltip__attr-value">{dateRange}</span>
        </div>

        {task.hasHoursConflict && (
          <div className="rp-task-info-tooltip__conflict-warning">
            <span className="rp-task-info-tooltip__conflict-icon">&#9888;</span>
            <span>
              Both hours columns set. Per-day ({task.hoursPerDay}h/d) used; total ({task.hoursTotal}h) ignored.
            </span>
          </div>
        )}

        {task.color && (
          <div className="rp-task-info-tooltip__attr">
            <span className="rp-task-info-tooltip__attr-label">Color</span>
            <span className="rp-task-info-tooltip__attr-value">
              <span
                className="rp-task-info-tooltip__color-swatch"
                style={{ backgroundColor: task.color }}
              />
            </span>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="rp-task-info-tooltip__actions">
        <Button
          kind={Button.kinds.SECONDARY}
          size={Button.sizes.SMALL}
          onClick={() => onEdit(task.id)}
        >
          Edit
        </Button>
        <Button
          size={Button.sizes.SMALL}
          onClick={() => onLogWork(task.id)}
        >
          Log Work
        </Button>
        <Button
          kind={Button.kinds.SECONDARY}
          size={Button.sizes.SMALL}
          onClick={() => onDuplicate(task.id)}
        >
          Duplicate
        </Button>
        <Button
          kind={Button.kinds.SECONDARY}
          size={Button.sizes.SMALL}
          color={Button.colors.NEGATIVE}
          onClick={() => {
            if (window.confirm('Are you sure you want to unplan this item?')) {
              onUnplan(task.id);
              onClose();
            }
          }}
        >
          Unplan
        </Button>
      </div>
    </div>
  );
};

export default TaskInfoTooltip;
